import type {
  ActionStruct,
  AppSpec,
  AppStruct,
  ConnectorSpec,
  ConnectorStruct,
  PerkIntegrationSpec,
} from '@cohort/shared/schema/apps/app';
import {PrestashopGroupPerkIntegrationSpec} from '@cohort/shared/schema/apps/prestashop/perks/group';
import {EmailSchema} from '@cohort/shared/schema/common';
import {z} from 'zod';

// Merchant Auth
export const PrestashopCredentialsSchema = z.object({
  shopUrl: z.string().url(),
  apiKey: z.string(),
  useSecureConnection: z.boolean(),
});
export type PrestashopCredentials = z.infer<typeof PrestashopCredentialsSchema>;

export type PrestashopConnectorStruct = ConnectorStruct<
  'prestashop',
  PrestashopCredentials,
  null,
  null
>;

// Actions

// Create Customer Group
export const PrestashopCreateCustomerGroupInputSchema = z.object({
  groupName: z.string(),
});
export type PrestashopCreateCustomerGroupInput = z.infer<
  typeof PrestashopCreateCustomerGroupInputSchema
>;

export const PrestashopCreateCustomerGroupOutputSchema = z.object({
  groupId: z.number(),
});
export type PrestashopCreateCustomerGroupOutput = z.infer<
  typeof PrestashopCreateCustomerGroupOutputSchema
>;

export type PrestashopCreateCustomerGroupActionType = ActionStruct<
  'create-customer-group',
  PrestashopCreateCustomerGroupInput,
  PrestashopCreateCustomerGroupOutput
>;

// Get customer by email
export const PrestashopGetCustomerByEmailInputSchema = z.object({
  email: EmailSchema,
});
export type PrestashopGetCustomerByEmailInput = z.infer<
  typeof PrestashopGetCustomerByEmailInputSchema
>;

export const PrestashopGetCustomerByEmailOutputSchema = z.object({
  customerId: z.number().int().nullable(),
});
export type PrestashopGetCustomerByEmailOutput = z.infer<
  typeof PrestashopGetCustomerByEmailOutputSchema
>;

export type PrestashopGetCustomerByEmailActionStruct = ActionStruct<
  'get-customer-by-email',
  PrestashopGetCustomerByEmailInput,
  PrestashopGetCustomerByEmailOutput
>;

// Add customer to group
export const PrestashopAddCustomerToGroupInputSchema = z.object({
  groupId: z.number().int(),
  customerId: z.number().int(),
});
export type PrestashopAddCustomerToGroupInput = z.infer<
  typeof PrestashopAddCustomerToGroupInputSchema
>;

export const PrestashopAddCustomerToGroupOutputSchema = z.object({});
export type PrestashopAddCustomerToGroupOutput = z.infer<
  typeof PrestashopAddCustomerToGroupOutputSchema
>;

export type PrestashopAddCustomerToGroupActionStruct = ActionStruct<
  'add-customer-to-group',
  PrestashopAddCustomerToGroupInput,
  PrestashopAddCustomerToGroupOutput
>;

// Check account creation
export const PrestashopCheckAccountCreationInputSchema = z.object({
  email: EmailSchema,
  groupId: z.number().int(),
});
export type PrestashopCheckAccountCreationInput = z.infer<
  typeof PrestashopCheckAccountCreationInputSchema
>;

export const PrestashopCheckAccountCreationOutputSchema = z.object({});
export type PrestashopCheckAccountCreationOutput = z.infer<
  typeof PrestashopCheckAccountCreationOutputSchema
>;

export type PrestashopCheckAccountCreationActionStruct = ActionStruct<
  'check-account-creation',
  PrestashopCheckAccountCreationInput,
  PrestashopCheckAccountCreationOutput
>;
// Remove customer from group
export const PrestashopRemoveCustomerFromGroupInputSchema = z.object({
  groupId: z.number().int(),
  customerId: z.number().int(),
});
export type PrestashopRemoveCustomerFromGroupInput = z.infer<
  typeof PrestashopRemoveCustomerFromGroupInputSchema
>;

export const PrestashopRemoveCustomerFromGroupOutputSchema = z.object({});
export type PrestashopRemoveCustomerFromGroupOutput = z.infer<
  typeof PrestashopRemoveCustomerFromGroupOutputSchema
>;

export type PrestashopRemoveCustomerFromGroupActionStruct = ActionStruct<
  'remove-customer-from-group',
  PrestashopRemoveCustomerFromGroupInput,
  PrestashopRemoveCustomerFromGroupOutput
>;

export const PrestashopConnectorSpec = {
  id: 'prestashop',
  type: 'custom',
  credentialsSchema: PrestashopCredentialsSchema,
  profileSchema: null,
  connectionConfigSchema: null,
} satisfies ConnectorSpec<PrestashopConnectorStruct>;

export const PrestashopPerkIntegrationSpecs = [
  PrestashopGroupPerkIntegrationSpec,
] as const satisfies ReadonlyArray<PerkIntegrationSpec>;
type PrestashopPerkIntegrationIds = (typeof PrestashopPerkIntegrationSpecs)[number]['id'];

export type PrestashopAppStruct = AppStruct<
  'prestashop',
  null,
  PrestashopConnectorStruct,
  null,
  null,
  never,
  never,
  PrestashopPerkIntegrationIds,
  never,
  never
>;

export const PrestashopAppSpec: AppSpec<PrestashopAppStruct> = {
  id: 'prestashop',
  name: 'Prestashop',
  appCredentialsSchema: null,
  merchantConnector: PrestashopConnectorSpec,
  userConnector: null,
  usersSyncSpec: null,
  actionSpecs: [],
  notificationIntegrationSpecs: [],
  perkIntegrationSpecs: PrestashopPerkIntegrationSpecs,
  triggerIntegrationSpecs: [],
  userEventSpecs: [],
};
